import {
  Box,
  Divider,
  HStack,
  IconButton,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  NumberInput,
  NumberInputField,
  Tooltip,
} from '@chakra-ui/react';
import {
  faCaretUp,
  faCheck,
  faCircleDot,
  faDrawPolygon,
  faMagicWandSparkles,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { useFlag } from '@unleash/proxy-client-react';
import { ReactComponent as PolylineIcon } from '../../assets/icons/draw-polyline.svg';
import { ReactComponent as RectangleIcon } from '../../assets/icons/draw-rectangle.svg';
import { ReactComponent as ExtrudeIcon } from '../../assets/icons/extrude-icon.svg';
import { useBimShapes } from '../../hooks/bim-shapes';
import { useViewer } from '../common/ForgeViewer';
import SparkelIcon from '../common/icon/SparkelIcon';
import { ShapeDrawingMode } from './shapes/ShapeDrawing';
import MagicPolygonCategoryMenu from './shapes/MagicPolygonCategoryMenu';
// eslint-disable-next-line complexity
export default function ShapesToolbar() {
  const { isAllModelsLoaded, loadedModels } = useViewer();
  const {
    drawing: {
      deactivate: deactivateDrawing,
      shapeToEdit,
      hasResult: hasDrawingResult,
      submit: submitDrawing,
      isSubmitting: isSubmittingDrawing,
      isDrawing,
      mode: drawingMode,
      activate: activateDrawing,
      isExtruding,
      setIsExtruding,
      thickness,
      setThickness,
      selectedCategories,
      setSelectedCategories,
    },
  } = useBimShapes();
  const backgroundColor = 'gray.800';
  const iconColor = 'gray.50';
  const borderColor = 'gray.700';
  const activeBackgroundColor = 'teal.700';
  const activeIconColor = 'teal.200';

  const { t } = useTranslation('project');

  const enableVolumetricShapes = useFlag('enable-volumetric-shapes');

  // Press and hold state for magic polygon menu
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const longPressTimerRef = useRef<number | null>(null);
  const hasTriggeredLongPressRef = useRef(false);

  useHotkeys('esc', deactivateDrawing, { enabled: isDrawing });
  useHotkeys('enter', submitDrawing, [submitDrawing], {
    enabled: isDrawing && hasDrawingResult && !isSubmittingDrawing,
  });
  useHotkeys('shift+s', () => activateDrawing(), [activateDrawing], {
    enabled: isAllModelsLoaded && Object.keys(loadedModels).length > 0,
  });
  useHotkeys(
    'space',
    async () => {
      await submitDrawing();
      activateDrawing();
    },
    { enabled: isDrawing && hasDrawingResult && !isSubmittingDrawing },
    [submitDrawing, activateDrawing]
  );

  useEffect(() => {
    if (thickness === undefined) {
      const defaultThickness = 1 * 3.2808; // convert to feet, since the viewer uses feet
      setThickness(defaultThickness);
    }
  }, [setThickness, thickness]);

  if (!isDrawing) {
    return null;
  }

  return (
    <HStack
      backgroundColor={backgroundColor}
      rounded={50}
      px={1}
      height={8}
      borderColor={borderColor}
      borderWidth="1px"
      mb={1}
    >
      <Tooltip label={t('shapes.toolbar.point')}>
        <IconButton
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          aria-label="draw point"
          size="xs"
          icon={
            <SparkelIcon
              icon={faCircleDot}
              color={
                drawingMode === ShapeDrawingMode.Point
                  ? activeIconColor
                  : iconColor
              }
            />
          }
          isActive={drawingMode === ShapeDrawingMode.Point}
          isDisabled={!!shapeToEdit}
          onClick={() => {
            activateDrawing(ShapeDrawingMode.Point);
          }}
        />
      </Tooltip>
      <Tooltip label={t('shapes.toolbar.polyline')}>
        <IconButton
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          aria-label="draw polyline"
          size="xs"
          icon={
            <SparkelIcon
              as={PolylineIcon}
              color={
                drawingMode === ShapeDrawingMode.LineString
                  ? activeIconColor
                  : iconColor
              }
            />
          }
          isActive={drawingMode === ShapeDrawingMode.LineString}
          isDisabled={!!shapeToEdit}
          onClick={() => {
            activateDrawing(ShapeDrawingMode.LineString);
          }}
        />
      </Tooltip>
      <Tooltip label={t('shapes.toolbar.polygon')}>
        <IconButton
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          aria-label="draw polygon"
          size="xs"
          icon={
            <SparkelIcon
              icon={faDrawPolygon}
              color={
                drawingMode === ShapeDrawingMode.Polygon
                  ? activeIconColor
                  : iconColor
              }
            />
          }
          isActive={drawingMode === ShapeDrawingMode.Polygon}
          isDisabled={!!shapeToEdit}
          onClick={() => {
            activateDrawing(ShapeDrawingMode.Polygon);
          }}
        />
      </Tooltip>
      <Tooltip label={t('shapes.toolbar.rectangle')}>
        <IconButton
          variant={'ghost'}
          colorScheme={'teal'}
          _active={{ backgroundColor: activeBackgroundColor }}
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          aria-label="draw rectangle"
          size="xs"
          icon={
            <SparkelIcon
              as={RectangleIcon}
              color={
                drawingMode === ShapeDrawingMode.Rectangle
                  ? activeIconColor
                  : iconColor
              }
            />
          }
          isActive={drawingMode === ShapeDrawingMode.Rectangle}
          isDisabled={!!shapeToEdit}
          onClick={() => {
            activateDrawing(ShapeDrawingMode.Rectangle);
          }}
        />
      </Tooltip>
      <Menu
        isOpen={isMenuOpen}
        onOpen={() => setIsMenuOpen(true)}
        onClose={() => {
          setIsMenuOpen(false);
          hasTriggeredLongPressRef.current = false;
        }}
        closeOnSelect={false}
        placement="bottom-start"
      >
        {({ isOpen }) => (
          <>
            <Tooltip
              label={t('shapes.toolbar.magic-polygon')}
              isDisabled={isOpen}
            >
              <MenuButton
                as={IconButton}
                variant={'ghost'}
                colorScheme={'teal'}
                _active={{ backgroundColor: activeBackgroundColor }}
                _hover={{ backgroundColor: 'blackAlpha.100' }}
                aria-label="draw magic polygon"
                size="xs"
                isActive={drawingMode === ShapeDrawingMode.MagicPolygon}
                isDisabled={!!shapeToEdit}
                width={10}
                onMouseDown={(e) => {
                  // Only handle left mouse button
                  if (e.button !== 0) return;

                  hasTriggeredLongPressRef.current = false;
                  longPressTimerRef.current = window.setTimeout(() => {
                    hasTriggeredLongPressRef.current = true;
                    setIsMenuOpen(true);
                  }, 400);
                }}
                onMouseUp={(e) => {
                  // Only handle left mouse button
                  if (e.button !== 0) return;

                  if (longPressTimerRef.current !== null) {
                    clearTimeout(longPressTimerRef.current);
                    longPressTimerRef.current = null;
                  }

                  // If long press was triggered, keep menu open and don't activate drawing
                  if (hasTriggeredLongPressRef.current) {
                    // Menu is already open from the timer, just prevent drawing
                    return;
                  }

                  // Normal click - activate drawing
                  activateDrawing(ShapeDrawingMode.MagicPolygon);
                }}
                onMouseLeave={() => {
                  // Clear timer if mouse leaves button
                  if (longPressTimerRef.current !== null) {
                    clearTimeout(longPressTimerRef.current);
                    longPressTimerRef.current = null;
                  }
                  // Don't reset hasTriggeredLongPressRef here - we want to keep menu open
                  // if it was opened via long press
                }}
                onClick={(e) => {
                  // Always prevent MenuButton's default click behavior
                  // We control menu opening/closing programmatically
                  e.preventDefault();
                  e.stopPropagation();
                }}
                icon={
                  <HStack spacing={1}>
                    <SparkelIcon
                      icon={faMagicWandSparkles}
                      color={
                        drawingMode === ShapeDrawingMode.MagicPolygon
                          ? activeIconColor
                          : iconColor
                      }
                    />
                    <Box
                      as="span"
                      fontSize="0.5rem"
                      lineHeight="1"
                      opacity={0.7}
                    >
                      <SparkelIcon
                        icon={faCaretUp}
                        color={
                          drawingMode === ShapeDrawingMode.MagicPolygon
                            ? activeIconColor
                            : iconColor
                        }
                      />
                    </Box>
                  </HStack>
                }
              />
            </Tooltip>
            <MagicPolygonCategoryMenu
              selectedCategories={selectedCategories}
              setSelectedCategories={setSelectedCategories}
            />
          </>
        )}
      </Menu>
      {enableVolumetricShapes &&
        (drawingMode === ShapeDrawingMode.Polygon ||
          drawingMode === ShapeDrawingMode.MagicPolygon) && (
          <>
            <Divider orientation="vertical" height={'70%'} />
            {isExtruding ? (
              <InputGroup size={'xs'}>
                <InputLeftElement>
                  <Tooltip label={'Remove Extrusion'}>
                    <IconButton
                      variant={'ghost'}
                      aria-label="remove extrude"
                      size="xs"
                      colorScheme="gray"
                      icon={
                        <SparkelIcon as={ExtrudeIcon} color={activeIconColor} />
                      }
                      _hover={{ backgroundColor: 'blackAlpha.100' }}
                      isDisabled={!!shapeToEdit}
                      onClick={() => {
                        setIsExtruding(false);
                      }}
                    />
                  </Tooltip>
                </InputLeftElement>
                <NumberInput
                  variant={'filled'}
                  max={100}
                  min={0.001} // Stop from setting zero-thickness
                  color={iconColor}
                  precision={3}
                  defaultValue={thickness ? thickness / 3.28084 : 1} // convert to meters
                  onChange={(value) => {
                    setThickness(parseFloat(value) * 3.28084); // convert to feet
                  }}
                >
                  <NumberInputField
                    paddingLeft={6}
                    paddingRight={0}
                    borderRadius={'md'}
                    autoFocus
                    width={24}
                    backgroundColor={borderColor}
                    _hover={{ backgroundColor: 'blackAlpha.500' }}
                  />
                </NumberInput>
              </InputGroup>
            ) : (
              <Tooltip label={'Extrude Polygon'}>
                <IconButton
                  variant={'ghost'}
                  colorScheme={'gray'}
                  _active={{ backgroundColor: activeBackgroundColor }}
                  _hover={{ backgroundColor: 'blackAlpha.100' }}
                  aria-label="extrude polygon"
                  size="xs"
                  icon={<SparkelIcon as={ExtrudeIcon} color={iconColor} />}
                  isDisabled={!!shapeToEdit}
                  onClick={() => {
                    setIsExtruding(true);
                  }}
                />
              </Tooltip>
            )}
          </>
        )}
      <Divider orientation="vertical" height={'70%'} />
      <Tooltip label={t('shapes.cancel-drawing')}>
        <IconButton
          aria-label="Cancel drawing"
          colorScheme="gray"
          _hover={{ backgroundColor: 'blackAlpha.100' }}
          backgroundColor={backgroundColor}
          size="xs"
          onClick={deactivateDrawing}
          isRound
          icon={<SparkelIcon icon={faTimes} color={iconColor} />}
        ></IconButton>
      </Tooltip>
      <Tooltip
        label={
          hasDrawingResult
            ? t('shapes.save-tooltip')
            : t('shapes.save-disabled-tooltip')
        }
      >
        <IconButton
          aria-label="Save"
          size="xs"
          onClick={submitDrawing}
          isLoading={isSubmittingDrawing}
          isRound
          isDisabled={!hasDrawingResult}
          icon={<SparkelIcon icon={faCheck} color={['gray.50', 'gray.700']} />}
        ></IconButton>
      </Tooltip>
    </HStack>
  );
}
